import { forwardRef } from 'react'
import type { TextareaHTMLAttributes } from 'react'
import { getNlsString } from '../../nls/nlsRuntime'

interface Props extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
  value?: string
  onChange?: (next: string) => void
  block?: boolean
  error?: boolean
  wrapperClassName?: string
  maxLength?: number
  /** Show the "{n} / {max}" footer counter when maxLength is set. Defaults to true. */
  showCount?: boolean
}

export const Textarea = forwardRef<HTMLTextAreaElement, Props>(function Textarea(
  {
    value,
    onChange,
    block = false,
    error = false,
    disabled = false,
    className,
    wrapperClassName,
    maxLength,
    showCount = true,
    ...rest
  },
  ref,
) {
  const wrapperClasses = [
    'dsTa',
    block ? 'dsTa--block' : '',
    error ? 'is-error' : '',
    disabled ? 'is-disabled' : '',
    wrapperClassName ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  const showFooter = maxLength != null && showCount

  const count = value?.length ?? 0

  const counterLabel = showFooter
    ? getNlsString('NLSCommonTextareaCounterLabel', { count: String(count), max: String(maxLength) })
    : ''
  const counterAriaLabel = showFooter
    ? getNlsString('NLSCommonTextareaCounterAriaLabel', { count: String(count), max: String(maxLength) })
    : ''

  return (
    <label className={wrapperClasses}>
      <textarea
        ref={ref}
        className={`dsTa__field ${className ?? ''}`.trim()}
        value={value}
        disabled={disabled}
        maxLength={maxLength}
        onChange={(event) => onChange?.(event.target.value)}
        {...rest}
      />
      {showFooter && (
        <div className="dsTa__foot" aria-label={counterAriaLabel} aria-live="polite">
          {counterLabel}
        </div>
      )}
    </label>
  )
})
